import { useState, useRef, useEffect } from 'react';
import { Play, Pause, Trophy, Zap, Clock, ArrowRight, CheckCircle2 } from 'lucide-react';
import { launchConfetti, showAchievement } from '../utils/effects.js';
import './Exercise.css';
import './ListeningExercise.css';

const formatTime = (time) => {
    if (isNaN(time) || time < 0) return '00:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

function ListeningExercise({ exerciseData, onCorrectAnswer }) {
    const { title, instruction, audio_url, questions = [], timeLimit = 180 } = exerciseData || {};

    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [isCorrectlyAnswered, setIsCorrectlyAnswered] = useState(false);
    const [incorrectAttempts, setIncorrectAttempts] = useState([]);
    const [feedback, setFeedback] = useState({ message: '', type: '' });
    
    const [points, setPoints] = useState(0);
    const [streak, setStreak] = useState(0);
    const [timeLeft, setTimeLeft] = useState(timeLimit);
    const [isTimerActive, setIsTimerActive] = useState(true);

    const currentQuestion = questions[currentQuestionIndex];
    const totalQuestions = questions.length;
    const progressPercentage = totalQuestions > 0 ? (currentQuestionIndex / totalQuestions) * 100 : 0;

    useEffect(() => {
        if (!isTimerActive || isCorrectlyAnswered) return;
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    setIsTimerActive(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [isTimerActive, isCorrectlyAnswered]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const setAudioData = () => setDuration(audio.duration);
        const updateTime = () => setCurrentTime(audio.currentTime);
        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleEnded = () => setIsPlaying(false);

        audio.addEventListener('loadedmetadata', setAudioData);
        audio.addEventListener('timeupdate', updateTime);
        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('loadedmetadata', setAudioData);
            audio.removeEventListener('timeupdate', updateTime);
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('ended', handleEnded);
        };
    }, []);

    useEffect(() => {
        setFeedback({ message: '', type: '' });
    }, [currentQuestionIndex]);


    const togglePlayPause = () => {
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
    };
    
    const handleSeek = (e) => {
        if (!audioRef.current || isNaN(audioRef.current.duration)) return;
        const progressBar = e.currentTarget;
        const clickPosition = e.clientX - progressBar.getBoundingClientRect().left;
        const newTime = (clickPosition / progressBar.offsetWidth) * duration;
        audioRef.current.currentTime = newTime;
    };

    const handleOptionSelect = (option) => {
        if (isCorrectlyAnswered || !currentQuestion) return;
        if (option === currentQuestion.answer) {
            setIsCorrectlyAnswered(true);
            setFeedback({ message: 'Correct! Well done.', type: 'correct' });
            setPoints(p => p + 10);
            setStreak(s => s + 1);
        } else {
            setStreak(0);
            if (!incorrectAttempts.includes(option)) {
                setIncorrectAttempts([...incorrectAttempts, option]);
            }
            setFeedback({ message: 'Incorrect answer. Keep trying!', type: 'incorrect' });
        }
    };

    const handleNextQuestion = () => {
        if (currentQuestionIndex >= questions.length - 1) {
            setIsTimerActive(false);
            onCorrectAnswer();
            showAchievement('Listening Complete!', '🎧');
            launchConfetti();
        } else {
            setCurrentQuestionIndex(prev => prev + 1);
            setIsCorrectlyAnswered(false);
            setIncorrectAttempts([]);
        }
    };

    const getOptionClass = (option) => {
        if (!currentQuestion) return '';
        if (isCorrectlyAnswered && option === currentQuestion.answer) return 'correct';
        if (incorrectAttempts.includes(option)) return 'incorrect';
        return '';
    };

    return (
        <div className="exercise-container">
            <div className="le-info-bar">
                <div className="le-info-item"><span>🏆</span> {points} pts</div>
                <div className="le-info-item"><span>⚡</span> Racha: {streak}</div>
                <div className={`le-info-item ${timeLeft <= 10 ? 'accent-bg' : ''}`}><span>🕒</span> {timeLeft}s</div>
            </div>

            <div className="le-progress-bar-container">
                <div className="le-progress-bar-fill" style={{ width: `${progressPercentage}%` }}></div>
            </div>
        
            <div className="le-header-card">
                <h2 className="exercise-title">{title || 'Listening Comprehension'}</h2>
                <p className="exercise-instruction">{instruction}</p>
            </div>

            <div className="le-audio-player">
                <audio ref={audioRef} src={audio_url} preload="metadata"></audio>
                <button className="le-play-btn" onClick={togglePlayPause}>
                    {isPlaying ? <Pause size={24} /> : <Play size={24} />}
                </button>
                <div className="le-audio-controls">
                    <div className="le-audio-info">
                        <span className="le-audio-title">Listen to the audio</span>
                        <span className="le-time-display">{formatTime(currentTime)} / {formatTime(duration)}</span>
                    </div>
                    <div className="le-audio-progress-bar" onClick={handleSeek}>
                        <div
                            className="le-audio-progress-fill"
                            style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
                        ></div>
                    </div>
                </div>
            </div>

            {currentQuestion ? (
                <>
                    <div className="le-question-wrapper">
                        <div className="le-question-number-badge">{currentQuestionIndex + 1}</div>
                        <p className="le-question-text">{currentQuestion.question}</p>
                    </div>

                    <div className="le-options">
                        {currentQuestion.options.map((option, index) => (
                            <div
                                key={index}
                                className={`le-option ${getOptionClass(option)}`}
                                onClick={() => handleOptionSelect(option)}
                            >
                                <span className="le-option-letter-badge">{String.fromCharCode(65 + index)}</span>
                                <span>{option}</span>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <div className="le-question-wrapper">
                    <p className="le-question-text">Este ejercicio de audio no contiene preguntas.</p>
                </div>
            )}
            
            {feedback.message && (
                <div className={`le-feedback ${feedback.type === 'correct' ? 'le-feedback-correct' : 'le-feedback-incorrect'}`}>
                    {feedback.message}
                </div>
            )}

            {isCorrectlyAnswered && (
                <div className="le-next-button-wrapper">
                    <button className="exercise-action-button" onClick={handleNextQuestion}>
                        {currentQuestionIndex < questions.length - 1 
                            ? (<>Next Question <ArrowRight size={20} /></>)
                            : (<>Finish <CheckCircle2 size={20} /></>)
                        }
                    </button>
                </div>
            )}
        </div>
    );
}

export default ListeningExercise;